import { denormalize } from 'normalizr';
import reduceReducers from 'reduce-reducers';

import { createEntitiesReducer } from 'modules/common/redux';

import { createNumericComparator } from 'utils/helpers';

import { types } from './actions';
import * as entities from './entities';

const initialState = {
  actionModalMode: null,
  actionModalCustomColumnId: null,
  activePurchasedComponentIds: [],
  customColumnIds: [],
  entities: {},
  isActionModalOpen: false,
  isCustomColumnsDrawerOpen: false,
  isCustomColumnDeleting: false,
  isCustomColumnSaving: false,
  isDrawerOpen: false,
  isFetchingPurchasedComponents: false,
  isUploadingCsv: false,
  page: 1,
  pageSize: 20,
  purchasedComponentCount: 0,
  searchString: '',
  sortString: null,
  tableData: {},
};

const entitiesReducer = createEntitiesReducer(
  ['purchasedComponents', 'customPurchasedComponentColumns'],
  [
    types.CUSTOM_COLUMNS_CREATE_SUCCESS,
    types.CUSTOM_COLUMNS_RECEIVE,
    types.CUSTOM_COLUMNS_REORDER_SUCCESS,
    types.CUSTOM_COLUMNS_UPDATE_SUCCESS,
    types.PURCHASED_COMPONENT_CREATE_SUCCESS,
    types.PURCHASED_COMPONENT_UPDATE_SUCCESS,
    types.PURCHASED_COMPONENTS_LIST_SUCCESS,
  ],
);

const reducer = (state, action) => {
  const { payload, type } = action;
  switch (type) {
    case types.ACTION_MODAL_CLOSE:
      return {
        ...state,
        actionModalCustomColumnId: null,
        isActionModalOpen: false,
      };
    case types.ACTION_MODAL_OPEN:
      return {
        ...state,
        actionModalMode: payload.mode,
        actionModalCustomColumnId: payload.customColumnId,
        isActionModalOpen: true,
      };
    case types.CUSTOM_COLUMNS_CREATE_REQUEST:
    case types.CUSTOM_COLUMNS_UPDATE_REQUEST:
      return {
        ...state,
        isCustomColumnSaving: true,
      };
    case types.CUSTOM_COLUMNS_CREATE_ERROR:
    case types.CUSTOM_COLUMNS_UPDATE_ERROR:
    case types.CUSTOM_COLUMNS_UPDATE_SUCCESS:
      return {
        ...state,
        isCustomColumnSaving: false,
      };
    case types.CUSTOM_COLUMNS_CREATE_SUCCESS:
      return {
        ...state,
        isCustomColumnSaving: false,
        customColumnIds: state.customColumnIds.concat([payload.result]),
      };
    case types.CUSTOM_COLUMNS_DELETE_REQUEST:
      return {
        ...state,
        isCustomColumnDeleting: true,
      };
    case types.CUSTOM_COLUMNS_DELETE_SUCCESS:
      return {
        ...state,
        customColumnIds: state.customColumnIds.filter(
          (id) => id != payload.customColumnId,
        ),
        isCustomColumnDeleting: false,
      };
    case types.CUSTOM_COLUMNS_DRAWER_CLOSE:
      return {
        ...state,
        isCustomColumnsDrawerOpen: false,
      };
    case types.CUSTOM_COLUMNS_DRAWER_OPEN:
      return {
        ...state,
        isCustomColumnsDrawerOpen: true,
      };
    case types.CUSTOM_COLUMNS_RECEIVE:
      return {
        ...state,
        customColumnIds: payload.result,
      };
    case types.LIST_PAGE_PAGINATE:
      return {
        ...state,
        page: payload.pageNumber,
        pageSize: payload.pageSize,
      };
    case types.LIST_PAGE_RECEIVE_TABLE_DATA:
      return {
        ...state,
        tableData: payload.tableData,
      };
    case types.LIST_PAGE_SEARCH:
      return {
        ...state,
        searchString: payload.searchString,
        page: 1,
      };
    case types.LIST_PAGE_SORT:
      return {
        ...state,
        sortString: payload.sortString,
        page: 1,
      };
    case types.PURCHASED_COMPONENTS_LIST_REQUEST:
      return {
        ...state,
        isFetchingPurchasedComponents: true,
      };
    case types.PURCHASED_COMPONENTS_LIST_SUCCESS:
      return {
        ...state,
        activePurchasedComponentIds: payload.result,
        isFetchingPurchasedComponents: false,
        purchasedComponentCount: payload.count,
      };
    case types.PURCHASED_COMPONENTS_LIST_ERROR:
      return {
        ...state,
        isFetchingPurchasedComponents: false,
      };
    case types.PURCHASED_COMPONENT_CREATE_SUCCESS:
      return {
        ...state,
        activePurchasedComponentIds: state.activePurchasedComponentIds.concat([
          payload.result,
        ]),
      };
    case types.PURCHASED_COMPONENT_DELETE_SUCCESS:
      return {
        ...state,
        activePurchasedComponentIds: state.activePurchasedComponentIds.filter(
          (id) => id != payload.purchasedComponentId,
        ),
      };
    case types.UPLOAD_PURCHASED_COMPONENTS_ERROR:
      return {
        ...state,
        isUploadingCsv: false,
      };
    case types.UPLOAD_PURCHASED_COMPONENTS_REQUEST:
      return {
        ...state,
        isUploadingCsv: true,
      };
    case types.UPLOAD_PURCHASED_COMPONENTS_SUCCESS:
      return {
        ...state,
        isUploadingCsv: false,
        page: 1,
        pageSize: 20,
        searchString: '',
      };
    default:
      return state;
  }
};

export default reduceReducers(entitiesReducer, reducer, initialState);

export const getScope = (state) => state.modules.purchasedComponents.listPage;

export const getPage = (state) => getScope(state).page;

export const getPageSize = (state) => getScope(state).pageSize;

export const getTotalPurchasedComponentCount = (state) =>
  getScope(state).purchasedComponentCount;

export const getActivePurchasedComponents = (state) =>
  denormalize(
    getScope(state).activePurchasedComponentIds,
    [entities.purchasedComponentSchema],
    getScope(state).entities,
  );

const getCustomColumns = (state) =>
  denormalize(
    getScope(state).customColumnIds,
    [entities.customColumnSchema],
    getScope(state).entities,
  );

export const getCustomColumnById = (state, customColumnId) =>
  getScope(state).entities.customPurchasedComponentColumns[customColumnId];

export const getSortedCustomColumns = (state) =>
  getCustomColumns(state).sort(createNumericComparator('position'));

export const getSortedCustomColumnIds = (state) =>
  getSortedCustomColumns(state).map(({ id }) => id);

export const isActionModalOpen = (state) => getScope(state).isActionModalOpen;

export const getActionModalMode = (state) => getScope(state).actionModalMode;

export const getActionModalCustomColumnId = (state) =>
  getScope(state).actionModalCustomColumnId;

export const isFetchingPurchasedComponents = (state) =>
  getScope(state).isFetchingPurchasedComponents;

export const isUploadingCsv = (state) => getScope(state).isUploadingCsv;

export const getSearchString = (state) => getScope(state).searchString;

export const getSortString = (state) => getScope(state).sortString;

export const getTableData = (state) => getScope(state).tableData;

export const getIsCustomColumnsDrawerOpen = (state) =>
  getScope(state).isCustomColumnsDrawerOpen;

export const getIsCustomColumnSaving = (state) =>
  getScope(state).isCustomColumnSaving;

export const getIsCustomColumnDeleting = (state) =>
  getScope(state).isCustomColumnDeleting;
